import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, ClockIcon, Loader2Icon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { useContactForm, ContactValues } from '../hooks/useContactForm';
import { clinic } from '../data/clinic';
const subjects = ['Afspraak voor intake', 'Vraag over een behandeling', 'Verwijzing', 'Overig'];
const inputClass = 'mt-2 w-full rounded-xl border bg-white px-4 py-3 text-[15px] text-ink placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary/20';
export function Contact() {
  const {
    values,
    errors,
    status,
    update,
    submit,
    reset
  } = useContactForm();
  const field = (name: keyof ContactValues) => ({
    id: name,
    value: values[name],
    'aria-invalid': Boolean(errors[name]),
    'aria-describedby': errors[name] ? `${name}-fout` : undefined,
    className: `${inputClass} ${errors[name] ? 'border-pain' : 'border-line focus:border-primary'}`
  });
  const details = [{
    icon: PhoneIcon,
    label: 'Telefoon',
    value: clinic.phone,
    href: clinic.phoneHref
  }, {
    icon: MailIcon,
    label: 'E-mail',
    value: clinic.email,
    href: `mailto:${clinic.email}`
  }, {
    icon: MapPinIcon,
    label: 'Adres',
    value: `${clinic.street}, ${clinic.postalCode} ${clinic.city}`
  }, {
    icon: ClockIcon,
    label: 'Bereikbaarheid',
    value: clinic.hours
  }];
  return <>
      <PageHeader title="Contact" crumbs={[{
      label: 'Contact'
    }]} intro="Heeft u een vraag of wilt u weten of onze behandelingen geschikt zijn voor uw situatie? Wij helpen u graag." data-id="element-445" data-mp="mhw2de2f3su9" />

      <section className="mx-auto grid max-w-7xl gap-12 px-5 py-16 lg:grid-cols-12 lg:gap-16 lg:px-8 lg:py-20" data-id="element-446" data-mp="vfxzhij161c9">
        <div className="lg:col-span-5" data-id="element-447" data-mp="10e9avxoz4jwl">
          <h2 className="font-display text-2xl font-semibold text-ink" data-id="element-448" data-mp="26wxr0bxk0c58">Onze kliniek in Velsen-Noord</h2>
          <ul className="mt-8 space-y-6" data-id="element-449" data-mp="2buptu0zqlio7">
            {details.map(({
            icon: Icon,
            label,
            value,
            href
          }) => <li key={label} className="flex gap-4" data-id="element-450" data-mp="1m6c1tcpe0yzr">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary" data-id="element-451" data-mp="1h8ahypk50vn8">
                  <Icon className="h-4 w-4" aria-hidden="true" data-id="element-452" data-mp="1w5t4rmd98ys1" />
                </span>
                <div className="min-w-0" data-id="element-453" data-mp="1r6q0i453mwl4">
                  <p className="text-sm text-muted" data-id="element-454" data-mp="1r6zqhrq593qt">{label}</p>
                  {href ? <a href={href} className="break-words font-medium text-ink hover:text-primary" data-id="element-455" data-mp="13hnk0zm7b9s7">
                      {value}
                    </a> : <p className="font-medium text-ink" data-id="element-456" data-mp="1d6x8m5hu6abk">{value}</p>}
                </div>
              </li>)}
          </ul>
          <div className="mt-10 overflow-hidden rounded-3xl border border-line" data-id="element-457" data-mp="18e467o9ube2n">
            <iframe title="Kaart met de locatie van Excellent Care Clinics" src="https://www.openstreetmap.org/export/embed.html?bbox=4.6290%2C52.4590%2C4.6590%2C52.4730&layer=mapnik&marker=52.4660%2C4.6440" className="h-64 w-full" loading="lazy" data-id="element-458" data-mp="kojn95dosgtd" />
          </div>
        </div>

        <div className="lg:col-span-7" data-id="element-459" data-mp="2hgz2r9o80p8q">
          <div className="rounded-3xl border border-line bg-white p-6 sm:p-10" data-id="element-460" data-mp="2hi1rpc1zghm0">
            <AnimatePresence mode="wait" initial={false} data-id="element-461" data-mp="2mglefeoehxff">
              {status === 'success' ? <motion.div key="success" initial={{
              opacity: 0,
              scale: 0.98
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.22,
              ease: [0.23, 1, 0.32, 1]
            }} className="py-12 text-center" role="status" data-id="element-462" data-mp="3a660w7jrjctg">
                  <CheckCircle2Icon className="mx-auto h-12 w-12 text-primary" aria-hidden="true" data-id="element-463" data-mp="1pp1da05hxq1m" />
                  <h2 className="mt-5 font-display text-2xl font-semibold text-ink" data-id="element-464" data-mp="1tr4qat7td2p0">Bedankt, {values.name.split(' ')[0]}</h2>
                  <p className="mx-auto mt-3 max-w-sm text-[15px] leading-relaxed text-muted" data-id="element-465" data-mp="29ysp9aolyzcs">
                    Wij hebben uw bericht ontvangen en nemen binnen twee werkdagen contact met u op.
                  </p>
                  <button type="button" onClick={reset} className="mt-8 rounded-full border border-line px-5 py-2.5 text-sm font-semibold text-ink hover:border-primary/40 hover:text-primary" data-id="element-466" data-mp="208hbkwf4hbsp">
                    Nieuw bericht
                  </button>
                </motion.div> : <motion.form key="form" onSubmit={submit} noValidate exit={{
              opacity: 0
            }} transition={{
              duration: 0.15
            }} data-id="element-467" data-mp="2ixea5chjyqgq">
                  <h2 className="font-display text-2xl font-semibold text-ink" data-id="element-468" data-mp="2ez6oc388eyyo">Stuur ons een bericht</h2>
                  <p className="mt-2 text-[15px] text-muted" data-id="element-469" data-mp="17h7egp0g9kr4">Velden met * zijn verplicht.</p>

                  <div className="mt-8 grid gap-5 sm:grid-cols-2" data-id="element-470" data-mp="13k1nl4rnvahp">
                    <div data-id="element-471" data-mp="3f6ljmmjj2sp5">
                      <label htmlFor="name" className="text-sm font-medium text-ink" data-id="element-472" data-mp="3f37ifhmlopja">Naam *</label>
                      <input type="text" autoComplete="name" onChange={e => update('name', e.target.value)} {...field('name')} data-id="element-473" data-mp="3j4t33p5dm841" />
                      {errors.name && <p id="name-fout" className="mt-1.5 text-sm text-pain" data-id="element-474" data-mp="3swysqs85odg2">{errors.name}</p>}
                    </div>
                    <div data-id="element-475" data-mp="1pm9m7npppxi">
                      <label htmlFor="phone" className="text-sm font-medium text-ink" data-id="element-476" data-mp="1q4bpkx22ydc">Telefoonnummer</label>
                      <input type="tel" autoComplete="tel" onChange={e => update('phone', e.target.value)} {...field('phone')} data-id="element-477" data-mp="6rlbte7pugsv" />
                    </div>
                    <div className="sm:col-span-2" data-id="element-478" data-mp="fp4515vik1fu">
                      <label htmlFor="email" className="text-sm font-medium text-ink" data-id="element-479" data-mp="fnsxqw2v0bjz">E-mailadres *</label>
                      <input type="email" autoComplete="email" onChange={e => update('email', e.target.value)} {...field('email')} data-id="element-480" data-mp="2hhjyvxb8u1p6" />
                      {errors.email && <p id="email-fout" className="mt-1.5 text-sm text-pain" data-id="element-481" data-mp="3kszelgdvho3x">{errors.email}</p>}
                    </div>
                    <div className="sm:col-span-2" data-id="element-482" data-mp="3fvfq7nngnuy9">
                      <label htmlFor="subject" className="text-sm font-medium text-ink" data-id="element-483" data-mp="3ttdzwfqu737f">Onderwerp</label>
                      <select onChange={e => update('subject', e.target.value)} {...field('subject')} data-id="element-484" data-mp="3oszrgwlbpr8v">
                        {subjects.map(s => <option key={s} data-id="element-485" data-mp="7jkzp8e1dgn5">{s}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2" data-id="element-486" data-mp="gehgam035vl1">
                      <label htmlFor="message" className="text-sm font-medium text-ink" data-id="element-487" data-mp="ggc3k6ytxzsa">Uw vraag *</label>
                      <textarea rows={5} onChange={e => update('message', e.target.value)} {...field('message')} data-id="element-488" data-mp="cf0a1qcu4uet" />
                      {errors.message && <p id="message-fout" className="mt-1.5 text-sm text-pain" data-id="element-489" data-mp="le3ryr09n3rq">{errors.message}</p>}
                    </div>
                  </div>

                  <p className="mt-5 text-xs leading-relaxed text-muted" data-id="element-490" data-mp="14x19qf40hq9i">
                    Deel geen uitgebreide medische gegevens via dit formulier. Wij bespreken uw situatie graag persoonlijk.
                  </p>

                  <button type="submit" disabled={status === 'submitting'} className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3.5 text-[15px] font-semibold text-white transition-colors duration-150 hover:bg-primary-dark disabled:opacity-70 sm:w-auto sm:px-8" data-id="element-491" data-mp="1001425b2qpix">
                    {status === 'submitting' && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-492" data-mp="h99zdh217nav" />}
                    {status === 'submitting' ? 'Versturen…' : 'Verstuur bericht'}
                  </button>
                </motion.form>}
            </AnimatePresence>
          </div>
        </div>
      </section>
    </>;
}